<template>
    <modal-dialog
        :value="true"
        :title="store ? store.name : $t('actions.new_store')"
        cancel-button
        ok-button
        :ok-text="okText"
        :ok-color="isDeletePath ? 'error' : undefined"
        :ok-loading="busy"
        :show-overlay="busy"
        @input="onDialogInput"
        @ok="submit"
    >
        <text-input
            v-model="form.name"
            v-validate="'required|max:255|space'"
            name="name"
            label="labels.store_name"
            :error-messages="errors.collect('name')"
            :data-vv-as="$t('labels.store_name')"
        ></text-input>
        <entity-select-input
            v-model="form.restaurants"
            v-validate="isDeletePath ? '' : 'required'"
            multi-select
            autocomplete
            name="restaurants"
            label="labels.store_restaurants"
            :entity="selectApiClientType.RESTAURANT"
            :items-filter="restaurantItemsFilter"
            :error-messages="errors.collect('restaurants')"
            :data-vv-as="$t('labels.store_restaurants')"
        ></entity-select-input>
        <v-alert v-if="isDeletePath" type="warning" dense text class="mt-2">
            {{ $t('texts.store_last_restaurant_removed') }}
        </v-alert>
    </modal-dialog>
</template>

<script>
import ModalDialog from '@/components/dialog/ModalDialog.vue'
import TextInput from '@/components/form/Inputs/TextInput.vue'
import EntitySelectInput from '@/components/form/Inputs/EntitySelectInput.vue'
import SelectApiClientType from '@/api/SelectApiClientType'
import RestApiCollection from '@/api/RestApiCollection'
import RestApiType from '@/api/RestApiType'
import Notification from '@/services/Notification/Notification'

const restaurantOption = (restaurant) => ({
    key: restaurant['@id'],
    label: `${restaurant.name} (id: ${restaurant.id})`,
    id: restaurant.id,
    name: restaurant.name,
})

export default {
    components: {
        ModalDialog,
        TextInput,
        EntitySelectInput,
    },
    props: {
        brandId: {
            type: Number,
            required: true,
        },
        store: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            selectApiClientType: SelectApiClientType,
            busy: false,
            form: {
                name: this.store ? this.store.name : '',
                restaurants: this.store
                    ? this.store.restaurants.map(
                          (restaurant) => restaurant['@id']
                      )
                    : [],
            },
        }
    },
    computed: {
        storeId() {
            return this.store ? this.store.id : null
        },
        isDeletePath() {
            return this.storeId !== null && this.form.restaurants.length === 0
        },
        okText() {
            return this.isDeletePath
                ? this.$t('actions.delete_store')
                : this.$t('actions.save')
        },
    },
    methods: {
        restaurantItemsFilter(items) {
            // only this brand's profiles; members of another store are
            // visible but disabled
            const filtered = items
                .filter((item) => item.brandId === this.brandId)
                .map((item) => ({
                    ...item,
                    disabled:
                        item.storeId !== null && item.storeId !== this.storeId,
                }))
            // keep current members the select-option list omits (hidden +
            // inactive profiles) – without them EntitySelectInput would
            // prune the value on load
            const known = new Set(items.map((item) => item.key))
            const missingMembers = this.store
                ? this.store.restaurants
                      .filter((restaurant) => !known.has(restaurant['@id']))
                      .map(restaurantOption)
                : []
            return [...missingMembers, ...filtered]
        },
        onDialogInput(open) {
            if (!open) this.$emit('closed')
        },
        async submit() {
            const isValid = await this.$validator.validateAll()
            if (!isValid) return

            this.busy = true
            const api = RestApiCollection.get(RestApiType.STORES)

            try {
                if (this.isDeletePath) {
                    await api.delete(this.storeId)
                    Notification.success(this.$t('texts.store_deleted'))
                    this.$emit('deleted', this.storeId)
                } else {
                    const payload = {
                        name: this.form.name,
                        restaurants: this.form.restaurants,
                    }
                    const saved = this.storeId
                        ? await api.update(payload, this.storeId)
                        : await api.create(payload)
                    Notification.success(this.$t('texts.store_saved'))
                    this.$emit('saved', saved)
                }
            } catch (reason) {
                this.showApiErrors(reason)
            } finally {
                this.busy = false
            }
        },
        showApiErrors(reason) {
            const response = reason?.response
            const violations = response?.data?.violations
            if (response?.status === 400 && Array.isArray(violations)) {
                violations.forEach((violation) => {
                    this.errors.add({
                        field: violation.propertyPath,
                        msg: violation.message,
                    })
                })
                return
            }
            // ResponseHandler already toasts !response, status >= 500 and
            // data.errors.message cases; only handle the remaining 4xx here
            if (response && response.status < 500) {
                Notification.error(
                    response.data?.['hydra:description'] ||
                        response.data?.message ||
                        'Request failed'
                )
            }
        },
    },
}
</script>
